import axios from 'axios';
import { appConfig } from '../../helpers/app-config/app-config';
import { getRefreshToken, getRequestToken, storeAuthenticationObject } from '../../helpers/storage/storage';
import type { RefreshResponse } from './auth-client.type';

/**
 * Module-level single-flight Promise for /auth/jwt/refresh/.
 *
 * Prevents the parallel-refresh-storm caused by `BLACKLIST_AFTER_ROTATION=True`
 * on the backend: if 5 in-flight API calls all 401 simultaneously, naïve
 * interceptor logic POSTs the same refresh token 5 times — the first call
 * blacklists it server-side, the other 4 get 401 "Token is blacklisted" and
 * force-log-out the user. Empirically reproducible: 10 parallel POSTs to
 * `/auth/jwt/refresh/` with the same R0 yielded 5×200 + 5×401 (race window
 * within ~120ms because simplejwt's blacklist write is non-atomic).
 *
 * The singleton collapses parallel callers into ONE in-flight refresh; all
 * others await its result. The promise is cleared on settle so the next 401
 * cascade (later in the session) starts a fresh refresh.
 *
 * What this DOES coordinate (single-tab):
 * - Multiple `*-client.ts` axios-instance interceptors firing concurrent 401s
 * - The mount-time refresh in `sign-in.tsx` and `useRefreshToken` hook
 * - Login flow's `useAuthTokenRefresh` hook
 *
 * What this DOES NOT coordinate (limitation):
 * - Cross-tab refreshes — `inFlight` is per-JS-runtime, so two tabs WILL each
 *   POST refresh and ONE will be blacklisted. A background tab with React
 *   Query refetchOnWindowFocus or a polling query can therefore log itself out
 *   if a foreground tab refreshes first. The persist guard below is careful not
 *   to make this WORSE: when a losing sibling clears the shared cookie, the tab
 *   whose refresh won still persists its valid rotation (a merely-empty stored
 *   token is not mistaken for a session change).
 *
 * What this DOES NOT do (deliberate):
 * - Use the project's `axiosInstance` factory. The instance has a response
 *   interceptor that, on 401 from any URL containing `/auth/jwt/refresh/`,
 *   calls `clearAuthenticationStore` and redirects to `/?token=expired`. By
 *   going around that interceptor here, the singleton's caller (the outer
 *   interceptor) can decide for itself how to handle the rejection.
 * - Run the project's request interceptor. The refresh endpoint is anonymous
 *   (the refresh token in the body IS the auth) so `Authorization: JWT ...`
 *   would be ignored anyway. If a future contributor adds non-auth request
 *   middleware (CSRF token, request signing, fingerprint header, Sentry
 *   breadcrumb, etc.) and that middleware MUST also fire on `/auth/jwt/refresh/`,
 *   they need to add it here too. This file's bare-axios usage is a
 *   trust-boundary contract — touch with care.
 *
 * Logout race (closed): `abortRefreshOnLogout()` flips a module-level flag
 * that prevents `storeAuthenticationObject` from running after the user
 * clicked logout. Without that gate, an in-flight refresh resolving AFTER
 * `clearAuthenticationStore` would silently re-establish the user's session
 * — a pen-test ID 01 closure violation.
 */

const REFRESH_URL = `${appConfig.VITE_APP_API_URL}/auth/jwt/refresh/`;

let inFlight: Promise<RefreshResponse> | null = null;
let abortedByLogout = false;
// Set when THIS tab's own load-time restore gate gives up (see
// `abortRefreshOnTimeout`). Reset at the start of every new single-flight —
// unlike logout, a restore timeout does NOT reload the page, so the flag must be
// scoped to the one refresh the gate abandoned and never leak into a later one.
let abortedByTimeout = false;

/**
 * Signals to the singleton that a logout is in progress and any currently
 * in-flight refresh must NOT write the rotated tokens back into cookies on
 * resolution. Called synchronously from `storage.logout()` before any other
 * teardown — module-level state survives microtask scheduling, whereas the
 * `LOGOUT_IN_PROGRESS_KEY` localStorage flag is cleared by `localStorage.clear()`
 * mid-logout and would no longer be visible to a microtask continuation.
 *
 * @returns {void}
 */
function abortRefreshOnLogout(): void {
  abortedByLogout = true;
}

/**
 * Signals that THIS tab's load-time session-restore gate has abandoned its
 * refresh (the `RESTORE_TIMEOUT_MS` deadline elapsed) and cleared the auth
 * store, so a late-resolving refresh must NOT write the rotated tokens back —
 * that would resurrect a session this tab deliberately gave up on.
 *
 * Per-tab by design: the flag lives in this JS runtime only, so it suppresses
 * persistence for the tab that timed out WITHOUT affecting a sibling tab whose
 * own concurrent refresh legitimately won (that tab has its own runtime where
 * this flag is false and must be allowed to persist its valid rotation — the
 * cross-tab "winner" case).
 *
 * @returns {void}
 */
function abortRefreshOnTimeout(): void {
  abortedByTimeout = true;
}

/**
 * Refreshes the JWT access+refresh tokens with single-flight deduplication.
 *
 * Side effects on success:
 * 1. Calls `storeAuthenticationObject` so the new tokens land in cookies
 *    before any caller reads them. Callers can then trust `getRefreshToken()`
 *    / `getRequestToken()` to return the rotated values.
 * 2. Verifies the cookie write actually persisted (Safari ITP, third-party
 *    cookie blocking, and quota errors all silent-fail in `js-cookie`).
 *    If the cookies did NOT persist, the singleton REJECTS rather than
 *    resolving — a false success would otherwise spiral into infinite 401s
 *    (retries read stale cookies → 401 → refresh again → loop).
 *
 * @param {string} token - The refresh token to exchange. Callers should pass
 *   `getRefreshToken()` at call time; subsequent parallel callers will receive
 *   the same shared promise regardless of what token they pass (the leader
 *   wins).
 * @returns {Promise<RefreshResponse>} Resolves with `{ access, refresh }` from
 *   the leader's response. Rejects with the underlying axios error, or with
 *   `Error('Token cookies failed to persist after refresh')` if cookie writes
 *   silent-failed.
 */
function refreshTokensSingleflight(token: string): Promise<RefreshResponse> {
  if (inFlight) return inFlight;

  // A fresh flight owns its own abandonment state. `abortedByTimeout` is only
  // meaningful for the specific refresh a restore gate gave up on; clearing it
  // here ensures a prior tab-timeout can never suppress a later, unrelated
  // refresh in this same (non-reloaded) runtime.
  abortedByTimeout = false;

  inFlight = (async () => {
    try {
      const response = await axios.post<RefreshResponse>(
        REFRESH_URL,
        { refresh: token },
        {
          headers: { 'Content-Type': 'application/json' },
          // Bare axios has NO default timeout (axios docs) so a hung backend
          // would leave `inFlight` set indefinitely, blocking every subsequent
          // 401-refresh in this tab on a dead Promise. Match AxiosInstance's
          // app-level setting (`axios-instance.ts:11`) so refresh fails the
          // same way other requests do — singleton then clears `inFlight` and
          // the interceptor's catch path redirects to login.
          timeout: 360000,
        },
      );

      // Validate response shape — defensive against backend changes that
      // could drop fields. Without this, `tokens.access = undefined` would
      // get written to cookies as the empty string (per js-cookie's `|| ''`
      // fallback in storeAuthenticationObject), silently logging users out.
      const { access, refresh } = response.data ?? {};
      if (typeof access !== 'string' || access.length === 0 || typeof refresh !== 'string' || refresh.length === 0) {
        throw new Error('Refresh response missing access or refresh token');
      }
      const tokens: RefreshResponse = { access, refresh };

      // Do NOT persist the rotated tokens if the session this refresh belonged
      // to is no longer the current one. Three cases require this:
      //   1. Logout-during-refresh: the user clicked logout after the POST
      //      started but before it resolved (`abortedByLogout`).
      //   2. This tab's restore timed out: its own load-time gate gave up and
      //      cleared the store (`abortedByTimeout`). Persisting the late result
      //      would resurrect a session THIS tab deliberately abandoned.
      //   3. A DIFFERENT session replaced ours mid-flight: the stored refresh
      //      token is present but no longer equals the one we exchanged (another
      //      login rotated it). Writing ours would clobber fresher credentials.
      // A merely-EMPTY stored token is deliberately NOT case 3: it means a
      // SIBLING tab cleared the shared cookie (its losing refresh 401'd) while
      // THIS tab's refresh won — that valid rotation MUST still be persisted, or
      // the winning tab is needlessly logged out. Hence `getRefreshToken() &&`:
      // skip only on a different, NON-empty token; leave the intentional
      // teardowns to cases 1 and 2.
      if (abortedByLogout || abortedByTimeout || (getRefreshToken() && getRefreshToken() !== token)) {
        return tokens;
      }

      storeAuthenticationObject(tokens);

      // Verify cookies actually persisted. js-cookie's `Cookies.set` is
      // silent-fail on Safari ITP, third-party-cookie blocking, quota
      // exceeded, etc. If the writes didn't take, retries that read cookies
      // (the request interceptor at axios-instance.ts) would get the OLD
      // access token and 401 again — an infinite spiral. Surface the failure
      // loudly so the outer interceptor can clearAuthenticationStore + redirect.
      if (getRequestToken() !== access || getRefreshToken() !== refresh) {
        throw new Error('Token cookies failed to persist after refresh');
      }

      return tokens;
    } finally {
      // Allow future cascades to start a fresh refresh. Leaving inFlight set
      // would deadlock the second cascade on a settled (and possibly rejected)
      // promise.
      inFlight = null;
    }
  })();

  return inFlight;
}

/**
 * Test-only: reset the in-flight singleton between tests so test order
 * doesn't matter. NOT for production use.
 *
 * Gated behind `import.meta.env.DEV` so this function is `undefined` in
 * production bundles (Vite tree-shakes the implementation out). Production
 * callers attempting to invoke it would get a TypeError, which is the
 * desired behavior for a test-only helper.
 *
 * @returns {void}
 */
const __resetRefreshSingletonForTests: (() => void) | undefined = import.meta.env.DEV
  ? () => {
      inFlight = null;
      abortedByLogout = false;
      abortedByTimeout = false;
    }
  : undefined;

export { __resetRefreshSingletonForTests, abortRefreshOnLogout, abortRefreshOnTimeout, refreshTokensSingleflight };
